import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CanDeactivate, Router, UrlTree } from '@angular/router';
import { WidgetNames, findWidgetWithName } from '@finxone-platform/shared/sys-config-types';
import { Observable, map, take } from 'rxjs';
import { ConfigService } from '../config-service/config-service.service';
import { WorkflowFormsService } from './workflow-forms.service';

@Injectable({
  providedIn: 'root',
})
export class WorkflowFormGuard implements CanDeactivate<any> {
  // Flag to determine if form validation should be enforced during navigation
  private static enforceValidation = true;

  constructor(
    private workflowForms: WorkflowFormsService,
    private configService: ConfigService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  /**
   * Enable form validation for the next navigation
   * Call this method before triggering navigation that should validate forms
   */
  static enableFormValidation(): void {
    WorkflowFormGuard.enforceValidation = true;
  }

  /**
   * Disable form validation for the next navigation
   * This is automatically called after each navigation attempt
   */
  static disableFormValidation(): void {
    WorkflowFormGuard.enforceValidation = false;
  }

  /**
   * Check if form validation is currently enabled
   */
  static isValidationEnabled(): boolean {
    return WorkflowFormGuard.enforceValidation;
  }

  canDeactivate(
    component: any,
    currentRoute: any,
    currentState: any,
    nextState?: any,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.configService
      .getCurrentRoleZoneWidgetsConfig()
      .pipe(take(1))
      .pipe(
        map((pageConfig) => {
          const formWidget = findWidgetWithName(WidgetNames.FORM_WIDGET, pageConfig.widgetLayouts);

          if (formWidget == null) {
            // No form widget found, allow navigation
            // as currentPageForm will be out of date so navigation gets blocked
            // If no form widget then this guard shouldn't run
            return true;
          }

          // Only validate forms if explicitly enabled
          if (WorkflowFormGuard.enforceValidation) {
            // Check if there are any forms registered on the current page
            const validation = this.workflowForms.validateCurrentPageForms();

            if (!validation.isValid) {
              // Mark all forms as touched to show validation errors
              this.workflowForms.markCurrentPageFormsTouched();

              return false;
            }
          }
          // If false, reset to true to enable check on next page navigation
          if (!WorkflowFormGuard.enforceValidation) WorkflowFormGuard.enableFormValidation();

          return true;
        }),
      );
  }
}
